import api from 'lib/trpcClient';
import useVehicle from '../vehicle/useVehicle';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { useEffect, useState } from 'react';

export default function useCurrencyRate({
  vehicleId,
  currency,
  date,
  defaultCurrencyRate,
  onRateChange,
}: {
  vehicleId: string;
  currency: CurrencyCode;
  date: CalendarDay;
  defaultCurrencyRate: CurrencyRateDetails | null;
  onRateChange: (rate: CurrencyRateDetails | null) => void;
}) {
  const [fxRate, setFxRate] = useState<CurrencyRateDetails | null>(
    defaultCurrencyRate,
  );

  const { vehicle } = useVehicle(vehicleId);

  const { mutate, isPending } = api.transactionFx.historicalRate.useMutation();

  const handleRateChange = (rate: CurrencyRateDetails | null) => {
    setFxRate(rate);
    onRateChange(rate);
  };

  const baseCurrency = vehicle?.baseCurrency;

  useEffect(() => {
    if (currency === baseCurrency) {
      handleRateChange(null);
    }
  }, [currency, baseCurrency]);

  useEffect(() => {
    if (fxRate || currency === baseCurrency) {
      return;
    }

    mutate(
      {
        vehicleId,
        transactionCurrency: currency,
        date: date.toUTCDate(),
      },
      {
        onSuccess: (data) => {
          if (!data || !vehicle)
            throw new Error('Failed to fetch historical rate.');

          const key = `${baseCurrency}${currency}`;

          if (data?.[key]) {
            const fetchedRate = data[key];
            handleRateChange({
              fetchedRate,
              fetchedSource: vehicle.fxDataFeed,
            });
          }
        },
      },
    );
  }, [defaultCurrencyRate]);

  return { isFetchingRate: isPending, fxRate, changeRate: handleRateChange };
}
